import React, { useState } from 'react';
export function AboutTab() {
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0, 1, 2]));
  const toggleSection = (index: number) => {
    const newOpenSections = new Set(openSections);
    if (newOpenSections.has(index)) {
      newOpenSections.delete(index);
    } else {
      newOpenSections.add(index);
    }
    setOpenSections(newOpenSections);
  };
  const sections = [{
    title: 'How to Use This Guide',
    content: <>
          <h4 className="text-purple-600 font-semibold mb-3" data-id="element-22">Navigation</h4>
          <p className="mb-3" data-id="element-23">
            Use the tabs at the top to switch between sections. Each section is
            designed for quick reference:
          </p>
          <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-24">
            <li data-id="element-25">
              <strong data-id="element-26">Treatment Protocols:</strong> Ready-to-use point
              combinations for common conditions
            </li>
            <li data-id="element-27">
              <strong data-id="element-28">Five Systems:</strong> Learn the theoretical framework
            </li>
            <li data-id="element-29">
              <strong data-id="element-30">Pattern Protocols:</strong> Constitutional patterns for
              complex cases
            </li>
            <li data-id="element-31">
              <strong data-id="element-32">Mirroring & Imaging:</strong> Advanced point selection
              techniques
            </li>
            <li data-id="element-33">
              <strong data-id="element-34">Hologram Theory:</strong> Body part correspondences
            </li>
            <li data-id="element-35">
              <strong data-id="element-36">Master Tung's Points:</strong> Detailed information on
              extraordinary points
            </li>
          </ul>
          <h4 className="text-purple-600 font-semibold mb-3" data-id="element-37">
            Search Function
          </h4>
          <p className="mb-3" data-id="element-38">
            Use the search bar at the top to instantly find information across
            all sections. Search by:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-39">
            <li data-id="element-40">Meridian names (e.g., "Large Intestine", "LI", "Yangming")</li>
            <li data-id="element-41">Point names (e.g., "LI-4", "Linggu")</li>
            <li data-id="element-42">Symptoms (e.g., "headache", "sciatica")</li>
            <li data-id="element-43">Body parts (e.g., "shoulder", "back")</li>
          </ul>
        </>
  }, {
    title: 'Clinical Technique Notes',
    content: <>
          <h4 className="text-purple-600 font-semibold mb-3" data-id="element-44">
            Essential Guidelines
          </h4>
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4" data-id="element-45">
            <strong data-id="element-46">Critical:</strong> Always obtain Qi sensation (De Qi).
            Without strong Qi arrival, the treatment will not be effective.
          </div>
          <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-47">
            <li data-id="element-48">
              <strong data-id="element-49">Needle Retention:</strong> 45-60 minutes typically, though
              results are often immediate
            </li>
            <li data-id="element-50">
              <strong data-id="element-51">Treatment Frequency:</strong> 2-3 times per week for acute
              conditions, 1-2 times per week for chronic
            </li>
            <li data-id="element-52">
              <strong data-id="element-53">Ashi Points:</strong> Always palpate the channels for
              tender spots and use them
            </li>
            <li data-id="element-54">
              <strong data-id="element-55">Test Immediately:</strong> Have the patient move or test
              the painful area after needle insertion
            </li>
            <li data-id="element-56">
              <strong data-id="element-57">Adjust if Needed:</strong> If no immediate improvement,
              try a different system or point
            </li>
          </ul>
          <h4 className="text-purple-600 font-semibold mb-3" data-id="element-58">
            Safety & Contraindications
          </h4>
          <ul className="list-disc pl-6 space-y-2" data-id="element-59">
            <li data-id="element-60">Many Master Tung points are contraindicated in pregnancy</li>
            <li data-id="element-61">Use appropriate needle gauge and length</li>
            <li data-id="element-62">Maintain sterile technique</li>
            <li data-id="element-63">Check for bleeding disorders or anticoagulant use</li>
            <li data-id="element-64">Be aware of local anatomy to avoid injury</li>
          </ul>
        </>
  }, {
    title: 'Glossary of Terms',
    content: <table className="w-full border-collapse" data-id="element-65">
          <tbody data-id="element-66">
            <tr className="border-b" data-id="element-67">
              <td className="p-2 font-semibold" data-id="element-68">Ashi Point</td>
              <td className="p-2" data-id="element-69">
                Tender point along a meridian; "ouch point"
              </td>
            </tr>
            <tr className="border-b" data-id="element-70">
              <td className="p-2 font-semibold" data-id="element-71">Bie-Jing</td>
              <td className="p-2" data-id="element-72">Branching channels; divergent meridians</td>
            </tr>
            <tr className="border-b" data-id="element-73">
              <td className="p-2 font-semibold" data-id="element-74">Biao-Li</td>
              <td className="p-2" data-id="element-75">
                Interior-Exterior relationship; Zang-Fu pairing
              </td>
            </tr>
            <tr className="border-b" data-id="element-76">
              <td className="p-2 font-semibold" data-id="element-77">De Qi</td>
              <td className="p-2" data-id="element-78">Arrival of Qi; needling sensation</td>
            </tr>
            <tr className="border-b" data-id="element-79">
              <td className="p-2 font-semibold" data-id="element-80">Distal Point</td>
              <td className="p-2" data-id="element-81">Point far from the affected area</td>
            </tr>
            <tr className="border-b" data-id="element-82">
              <td className="p-2 font-semibold" data-id="element-83">Jueyin</td>
              <td className="p-2" data-id="element-84">
                Reverting Yin; Liver and Pericardium channels
              </td>
            </tr>
            <tr className="border-b" data-id="element-85">
              <td className="p-2 font-semibold" data-id="element-86">Shaoyang</td>
              <td className="p-2" data-id="element-87">
                Lesser Yang; Gallbladder and Triple Heater channels
              </td>
            </tr>
            <tr className="border-b" data-id="element-88">
              <td className="p-2 font-semibold" data-id="element-89">Shaoyin</td>
              <td className="p-2" data-id="element-90">Lesser Yin; Heart and Kidney channels</td>
            </tr>
            <tr className="border-b" data-id="element-91">
              <td className="p-2 font-semibold" data-id="element-92">Taiyang</td>
              <td className="p-2" data-id="element-93">
                Greater Yang; Bladder and Small Intestine channels
              </td>
            </tr>
            <tr className="border-b" data-id="element-94">
              <td className="p-2 font-semibold" data-id="element-95">Taiyin</td>
              <td className="p-2" data-id="element-96">Greater Yin; Spleen and Lung channels</td>
            </tr>
            <tr data-id="element-97">
              <td className="p-2 font-semibold" data-id="element-98">Yangming</td>
              <td className="p-2" data-id="element-99">
                Bright Yang; Stomach and Large Intestine channels
              </td>
            </tr>
          </tbody>
        </table>
  }];
  return <div className="bg-gray-50 p-6 rounded-xl shadow-md" data-id="element-100">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 pb-3 border-b-4 border-purple-600" data-id="element-101">
        About & Guide
      </h2>
      <div className="space-y-4" data-id="element-102">
        {sections.map((section, index) => <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-103">
            <button onClick={() => toggleSection(index)} className="w-full p-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-104">
              <span className="font-semibold text-lg text-gray-800" data-id="element-105">
                {section.title}
              </span>
              <span className={`transform transition-transform ${openSections.has(index) ? 'rotate-180' : ''}`} data-id="element-106">
                ▼
              </span>
            </button>
            {openSections.has(index) && <div className="p-6 border-t border-gray-200 text-gray-700" data-id="element-107">
                {section.content}
              </div>}
          </div>)}
      </div>
    </div>;
}